import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { Box, Button, IconButton, TextField } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { BookOpen } from "lucide-react";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  ExperienceFieldGuide,
  ExperienceFieldLabel,
  ExperienceHeader,
  ExperienceMediaCard,
  ExperienceOptionalLabel,
  ExperienceShell,
  type ExperienceMomentVariant,
  fieldHelperText,
  getExperienceFieldSx,
  getFloatingActionSx,
  getPrimaryButtonSx,
  isSeasonMomentVariant,
} from "./ExperienceUI";

interface AboutData {
  title?: string;
  description?: string;
  descriptionImage?: string | File;
  extraDescription?: string;
  extraDescriptionImage?: string | File;
  moodNote?: string;
  momentImage?: string | File;
  moments?: string[];
  credits?: string[];
}

interface AboutExperienceProps {
  darkMode: boolean;
  data?: AboutData;
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: AboutData) => void;
}

const hasMediaValue = (value?: string | File) =>
  value instanceof File || (typeof value === "string" && value.trim() !== "");

const emptySeasonAboutData: AboutData = {
  title: "",
  description: "",
  descriptionImage: "",
  extraDescription: "",
  extraDescriptionImage: "",
  moodNote: "",
  momentImage: "",
  moments: [],
  credits: [],
};

const getValidationSchema = (t: any, isSeason: boolean) =>
  isSeason
    ? Yup.object({
        title: Yup.string().nullable(),
        description: Yup.string().nullable(),
        extraDescription: Yup.string().nullable(),
        descriptionImage: Yup.mixed().nullable(),
        extraDescriptionImage: Yup.mixed().nullable(),
        momentImage: Yup.mixed().nullable(),
        moodNote: Yup.string().nullable(),
      }).test(
        "about-has-story",
        t("about_story_required_season"),
        function hasAboutStory() {
          const values = this.parent as AboutData;
          const strip = (v?: string) => (v || "").replace(/<[^>]*>/g, " ").trim();
          return (
            strip(values.title).length > 0 ||
            strip(values.description).length > 0 ||
            strip(values.extraDescription).length > 0
          );
        },
      )
    : Yup.object({
    title: Yup.string()
      .required(t("title_is_required"))
      .min(3, t("title_must_be_at_least_3_characters")),
    description: Yup.string()
      .required(t("description_is_required"))
      .min(10, t("description_must_be_at_least_10_characters")),
    extraDescription: Yup.string()
      .required(t("description_is_required"))
      .min(10, t("description_must_be_at_least_10_characters")),
    descriptionImage: Yup.mixed().test(
      "description-image-required",
      t("image_is_required"),
      (value) => hasMediaValue(value as string | File | undefined)
    ),
    extraDescriptionImage: Yup.mixed().test(
      "extra-description-image-required",
      t("image_is_required"),
      (value) => hasMediaValue(value as string | File | undefined)
    ),
    momentImage: Yup.mixed().test(
      "moment-image-required",
      t("image_is_required"),
      (value) => hasMediaValue(value as string | File | undefined)
    ),
    moodNote: Yup.string()
      .required(t("description_is_required"))
      .min(3, t("description_is_required")),
  });

const AboutExperience: React.FC<AboutExperienceProps> = ({
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");
  const isSeason = isSeasonMomentVariant(momentVariant);
  const validationSchema = getValidationSchema(t, isSeason);
  const isInternalUpdateRef = useRef(false);
  const isSyncingFromParentRef = useRef(false);
  const fieldSx = getExperienceFieldSx(darkMode);
  const standardDefaults: AboutData = {
    title: t("about_default_title"),
    description: t("about_default_description"),
    descriptionImage: "",
    extraDescription: t("about_default_extra_description"),
    extraDescriptionImage: "",
    moodNote: t("about_default_mood_note"),
    momentImage: "",
    moments: [""],
    credits: [""],
  };
  const defaults = isSeason ? emptySeasonAboutData : standardDefaults;
  const resolvedData = useMemo<AboutData>(
    () => ({
      title: data?.title ?? defaults.title,
      description: data?.description ?? defaults.description,
      descriptionImage:
        data?.descriptionImage ?? defaults.descriptionImage,
      extraDescription:
        data?.extraDescription ?? defaults.extraDescription,
      extraDescriptionImage:
        data?.extraDescriptionImage ?? defaults.extraDescriptionImage,
      moodNote: data?.moodNote ?? defaults.moodNote,
      momentImage: data?.momentImage ?? defaults.momentImage,
      moments: data?.moments ?? defaults.moments,
      credits: data?.credits ?? defaults.credits,
    }),
    [
      data?.title,
      data?.description,
      data?.descriptionImage,
      data?.extraDescription,
      data?.extraDescriptionImage,
      data?.moodNote,
      data?.momentImage,
      data?.moments,
      data?.credits,
      isSeason,
    ]
  );

  const formik = useFormik<AboutData>({
    initialValues: resolvedData,
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: !isSeason,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const hasChanged = (
        [
          "title",
          "description",
          "descriptionImage",
          "extraDescription",
          "extraDescriptionImage",
          "moodNote",
          "momentImage",
        ] as Array<keyof AboutData>
      ).some((key) => formik.values[key] !== resolvedData[key]);

      const momentsChanged =
        JSON.stringify(formik.values.moments || []) !==
        JSON.stringify(resolvedData.moments || []);
      const creditsChanged =
        JSON.stringify(formik.values.credits || []) !==
        JSON.stringify(resolvedData.credits || []);

      if (hasChanged || momentsChanged || creditsChanged) {
        isSyncingFromParentRef.current = true;
        formik.setValues(resolvedData);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, resolvedData]);

  useEffect(() => {
    if (!onDataChange) return;
    if (isSyncingFromParentRef.current) {
      const isSynced =
        JSON.stringify(formik.values) === JSON.stringify(resolvedData);
      if (isSynced) {
        isSyncingFromParentRef.current = false;
      }
      return;
    }

    const hasChangedFromParent =
      JSON.stringify(formik.values) !== JSON.stringify(resolvedData);
    if (!hasChangedFromParent) return;

    const timeoutId = setTimeout(() => {
      isInternalUpdateRef.current = true;
      onDataChange({
        ...formik.values,
        moments: (formik.values.moments || []).filter(
          (item) => item.trim() !== ""
        ),
        credits: (formik.values.credits || []).filter(
          (item) => item.trim() !== ""
        ),
      });
    }, 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, onDataChange, resolvedData]);

  const handleImageSelect = (
    files: FileList | null,
    field: "descriptionImage" | "extraDescriptionImage" | "momentImage"
  ) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      formik.setFieldValue(field, reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const renderImageUploader = (
    field: "descriptionImage" | "extraDescriptionImage" | "momentImage",
    label: string,
    optional = false,
  ) => {
    const value = formik.values[field];
    const inputId = `${field}-upload`;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <ExperienceFieldLabel darkMode={darkMode}>
          {label}
          {optional && <ExperienceOptionalLabel darkMode={darkMode} />}
        </ExperienceFieldLabel>
        {typeof value === "string" && value ? (
          <ExperienceMediaCard darkMode={darkMode}>
            <Box sx={{ position: "relative" }}>
              <Box
                component="img"
                src={value}
                alt={label}
                sx={{
                  width: "100%",
                  height: 180,
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <IconButton
                onClick={() => formik.setFieldValue(field, "")}
                sx={{
                  ...getFloatingActionSx(darkMode),
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </ExperienceMediaCard>
        ) : null}
        <input
          id={inputId}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            handleImageSelect(e.target.files, field);
            e.target.value = "";
          }}
        />
        <label htmlFor={inputId}>
          <Button
            variant="contained"
            component="span"
            sx={{ ...getPrimaryButtonSx(darkMode), alignSelf: "flex-start" }}
          >
            {value ? t("replace_image") : t("upload_image")}
          </Button>
        </label>
      </Box>
    );
  };

  const renderArrayFields = (
    field: "moments" | "credits",
    label: string,
    addLabel: string
  ) => {
    const items = formik.values[field] || [];
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <ExperienceFieldLabel darkMode={darkMode}>{label}</ExperienceFieldLabel>
        {items.map((item, index) => (
          <Box key={`${field}-${index}`} sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={item}
              sx={fieldSx}
              onChange={(e) => {
                const nextItems = [...items];
                nextItems[index] = e.target.value;
                formik.setFieldValue(field, nextItems);
              }}
              placeholder={t("about_array_item_placeholder", {
                label,
                index: index + 1,
              })}
            />
            <IconButton
              onClick={() => {
                const nextItems = items.filter(
                  (_, itemIndex) => itemIndex !== index
                );
                formik.setFieldValue(
                  field,
                  nextItems.length ? nextItems : [""]
                );
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="text"
          startIcon={<AddIcon />}
          onClick={() => formik.setFieldValue(field, [...items, ""])}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          {addLabel}
        </Button>
      </Box>
    );
  };

  return (
    <FormikProvider value={formik}>
      <ExperienceShell>
        {!isSeason && (
          <ExperienceHeader
            darkMode={darkMode}
            icon={BookOpen}
            title={t("about")}
            subtitle={t("about_header_subtitle")}
          />
        )}

        <ExperienceFieldGuide darkMode={darkMode}>
          {isSeason
            ? t("about_field_guide_season")
            : t("about_field_guide")}
        </ExperienceFieldGuide>

        <TextField
          label={
            <>
              {isSeason ? t("about_title_label_season") : t("about_title_label")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          name="title"
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={isSeason ? t("about_title_placeholder_season") : undefined}
          error={Boolean(formik.touched.title && formik.errors.title)}
          helperText={fieldHelperText(
            formik.touched.title && formik.errors.title
              ? String(formik.errors.title)
              : undefined,
            isSeason ? t("about_title_helper_season") : " ",
          )}
          sx={fieldSx}
        />

        <TextField
          fullWidth
          multiline
          minRows={4}
          label={isSeason ? t("about_description_label_season") : t("about_description_label")}
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={
            isSeason
              ? t("about_description_placeholder_season")
              : t("about_description_placeholder")
          }
          error={Boolean(
            formik.touched.description && formik.errors.description,
          )}
          helperText={fieldHelperText(
            formik.touched.description && formik.errors.description
              ? String(formik.errors.description)
              : undefined,
            isSeason
              ? t("about_description_helper_season")
              : t("about_description_helper"),
          )}
          sx={fieldSx}
        />

        {renderImageUploader(
          "descriptionImage",
          isSeason ? t("about_cover_photo") : t("about_main_description_image"),
          isSeason,
        )}

        {!isSeason && (
          <>
        <TextField
          label={t("about_extra_story")}
          name="extraDescription"
          value={formik.values.extraDescription}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          multiline
          minRows={4}
          fullWidth
          placeholder={t("about_extra_story_placeholder")}
          sx={fieldSx}
        />

        {renderImageUploader("extraDescriptionImage", t("about_extra_story_image"))}

        <TextField
          label={t("about_mood_note")}
          name="moodNote"
          value={formik.values.moodNote}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          multiline
          minRows={2}
          fullWidth
          placeholder={t("about_mood_note_placeholder")}
          sx={fieldSx}
        />

        {renderImageUploader("momentImage", t("about_moment_image"))}

        {renderArrayFields("moments", t("about_recording_moments"), t("about_add_moment"))}
        {renderArrayFields("credits", t("about_credits"), t("about_add_credit"))}
          </>
        )}
      </ExperienceShell>
    </FormikProvider>
  );
};

export default AboutExperience;
